import {useCallback, useMemo, useState} from "react";

import {
    FetchPolicy,
    useCachedEffect,
    type Result,
} from "@khanacademy/wonder-blocks-data";
import {useGql} from "@khan/data-gql";

import {dismiss, isDismissed, type Expiration} from "./dismissible-api.ts";

export function useDismissible(dismissKey: string): {
    isDismissedResult: Result<boolean>;
    dismiss: (expiresInDays: Expiration) => void;
} {
    const gqlFetch = useGql();
    const [result] = useCachedEffect<boolean>(
        `dismissible-${dismissKey}`,
        () => isDismissed(gqlFetch, dismissKey),
        {
            fetchPolicy: FetchPolicy.NetworkOnly,
        },
    );

    // this ensures the dismissed item is hidden as soon as the dismiss call is
    // made, rather than waiting for the cache to update
    const [wasDismissCalled, setWasDismissCalled] = useState(false);
    const isDismissedResult = useMemo<Result<boolean>>(
        () => (wasDismissCalled ? {status: "success", data: true} : result),
        [result, wasDismissCalled],
    );

    const dismissCallback = useCallback(
        (expiresInDays: Expiration) => {
            dismiss(gqlFetch, dismissKey, expiresInDays);
            setWasDismissCalled(true);
        },
        [dismissKey, gqlFetch],
    );

    return {
        isDismissedResult,
        dismiss: dismissCallback,
    };
}
